import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Play, Trash2, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  type: "html",
  url: "",
  restaurant_name: "",
  address: "",
  lat: "",
  lng: "",
  cuisine: "",
  image_url: "",
  opening_hours: "",
  extraction_prompt: "",
  extraction_mode: "legacy_auto",
  extraction_config: "{}",
  notes: "",
};

export default function SourcesPage() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState({});
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sources");
      setSources(r.data);
    } catch { toast.error("Failed to load sources"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      type: s.type, url: s.url, restaurant_name: s.restaurant_name,
      address: s.address || "", lat: String(s.lat), lng: String(s.lng),
      cuisine: s.cuisine || "", image_url: s.image_url || "", opening_hours: s.opening_hours || "", extraction_prompt: s.extraction_prompt || "", extraction_mode: s.extraction_mode || "legacy_auto", extraction_config: JSON.stringify(s.extraction_config || {}, null, 2), notes: s.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const address = form.address.trim();
    const lat = form.lat.trim();
    const lng = form.lng.trim();
    const originalAddress = (editing?.address || "").trim();
    const addressChanged = Boolean(editing) && address !== originalAddress;
    const coordinatesUnchanged = Boolean(editing) && lat === String(editing.lat) && lng === String(editing.lng);
    const shouldGeocodeFromAddress = Boolean(address && ((!lat && !lng) || (addressChanged && coordinatesUnchanged)));
    const shouldUseManualCoordinates = lat || lng;
    if (!address && (!lat || !lng)) {
      toast.error("Enter an address for automatic coordinates, or provide both latitude and longitude manually.");
      return;
    }
    if (!shouldGeocodeFromAddress && ((lat && !lng) || (!lat && lng))) {
      toast.error("Please provide both latitude and longitude for a manual override.");
      return;
    }
    let parsedExtractionConfig = {};
    try {
      parsedExtractionConfig = form.extraction_config.trim() ? JSON.parse(form.extraction_config) : {};
    } catch {
      toast.error("Extraction config must be valid JSON.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        address: address || null,
        cuisine: form.cuisine || null,
        image_url: form.image_url || null,
        opening_hours: form.opening_hours || null,
        extraction_prompt: form.extraction_prompt || null,
        extraction_mode: form.extraction_mode || "legacy_auto",
        extraction_config: parsedExtractionConfig,
        notes: form.notes || null,
      };
      if (shouldUseManualCoordinates && !shouldGeocodeFromAddress) payload.lat = Number(lat);
      else delete payload.lat;
      if (shouldUseManualCoordinates && !shouldGeocodeFromAddress) payload.lng = Number(lng);
      else delete payload.lng;
      if (editing) {
        await api.put(`/admin/sources/${editing.id}`, payload);
        toast.success("Source updated");
      } else {
        await api.post("/admin/sources", payload);
        toast.success("Source added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/sources/${toDelete.id}`);
      toast.success(`Deleted ${toDelete.restaurant_name}`);
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
    setDeleting(false);
  };

  const extract = async (s) => {
    const previousRun = s.last_run_at || null;
    setExtracting((e) => ({ ...e, [s.id]: true }));
    try {
      await api.post(`/admin/sources/${s.id}/extract`);
      toast.success(`Extraction queued for ${s.restaurant_name}`);

      // Extraction runs in a backend background task. Poll until this source's
      // last_run_at changes so the UI shows the real result instead of stopping
      // the spinner after an arbitrary short timeout.
      for (let attempt = 0; attempt < 12; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const r = await api.get("/admin/sources");
        setSources(r.data);
        const updated = r.data.find((item) => item.id === s.id);
        if (updated && updated.last_run_at && updated.last_run_at !== previousRun) {
          const message = updated.last_message || `Extraction finished for ${s.restaurant_name}`;
          if (updated.last_status === "ok") toast.success(message);
          else toast.error(message);
          return;
        }
      }
      toast.warning(`Extraction is still running for ${s.restaurant_name}. Check logs in a moment.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue extraction");
    } finally {
      setExtracting((e) => ({ ...e, [s.id]: false }));
    }
  };

  return (
    <div className="px-4 py-5 sm:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Sources</h1>
          <p className="text-[#6A6F5C] text-sm mt-1">Restaurant pages we scan for lunch specials.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="w-full bg-[#E07A5F] hover:bg-[#cf6b51] sm:w-auto" data-testid="add-source-btn">
              <Plus size={16} className="mr-1.5" /> Add source
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Edit source" : "Add new source"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="source-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="html">HTML page</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="image">Image (jpg/png)</SelectItem>
                    <SelectItem value="instagram">Instagram profile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>URL</Label>
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required data-testid="source-url" />
              </div>
              <div>
                <Label>Restaurant name</Label>
                <Input value={form.restaurant_name} onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })} required data-testid="source-name" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="source-address" />
                <p className="mt-1 text-xs text-[#6A6F5C]">
                  Coordinates are determined automatically from this address. If you edit the address, coordinates are refreshed unless you change the manual fields below.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label>Latitude (optional/manual)</Label>
                  <Input type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="source-lat" />
                </div>
                <div>
                  <Label>Longitude (optional/manual)</Label>
                  <Input type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="source-lng" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label>Cuisine</Label>
                  <Input value={form.cuisine} onChange={(e) => setForm({ ...form, cuisine: e.target.value })} placeholder="Italian, Asian…" />
                </div>
                <div>
                  <Label>Card image URL</Label>
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" />
                </div>
              </div>
              <div>
                <Label>Opening hours</Label>
                <Input value={form.opening_hours} onChange={(e) => setForm({ ...form, opening_hours: e.target.value })} placeholder="Mo-Fr 11:30-14:30" data-testid="source-opening-hours" />
              </div>
              <div>
                <Label>Extraction mode</Label>
                <Select value={form.extraction_mode} onValueChange={(v) => setForm({ ...form, extraction_mode: v })}>
                  <SelectTrigger data-testid="source-extraction-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="legacy_auto">Legacy auto</SelectItem>
                    <SelectItem value="html_text">HTML text</SelectItem>
                    <SelectItem value="image_menu">Image menu</SelectItem>
                    <SelectItem value="pdf_text">PDF text</SelectItem>
                    <SelectItem value="pdf_image">PDF image</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-[#6A6F5C]">
                  Determines which generic extractor runs. Restaurant-specific settings stay in the database.
                </p>
              </div>
              <div>
                <Label>Extraction config JSON</Label>
                <Textarea
                  value={form.extraction_config}
                  onChange={(e) => setForm({ ...form, extraction_config: e.target.value })}
                  placeholder={'{"content_selector":"#mittagstisch","ignore_selectors":[".footer"]}'}
                  rows={6}
                  data-testid="source-extraction-config"
                />
                <p className="mt-1 text-xs text-[#6A6F5C]">
                  Mode-specific selectors and rules, stored on this source record only.
                </p>
              </div>
              <div>
                <Label>Restaurant-specific extraction prompt</Label>
                <Textarea
                  value={form.extraction_prompt}
                  onChange={(e) => setForm({ ...form, extraction_prompt: e.target.value })}
                  placeholder="Optional: e.g. ignore catering boxes; only extract the weekly lunch table."
                  rows={4}
                  data-testid="source-extraction-prompt"
                />
                <p className="mt-1 text-xs text-[#6A6F5C]">
                  Optional instructions appended to the global extraction prompt for this restaurant only.
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#3D4035] hover:bg-[#2c2f25]" data-testid="save-source-btn">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {editing ? "Save changes" : "Add source"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[rgba(61,64,53,0.08)] rounded-xl overflow-hidden overflow-x-auto">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow className="bg-[#F7F5F0]">
              <TableHead>Restaurant</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Last run</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#6A6F5C]">Loading…</TableCell></TableRow>
            ) : sources.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#6A6F5C]">No sources yet — add your first one.</TableCell></TableRow>
            ) : sources.map((s) => (
              <TableRow key={s.id} data-testid={`source-row-${s.id}`}>
                <TableCell>
                  <div className="font-medium">{s.restaurant_name}</div>
                  {s.address && <div className="text-xs text-[#6A6F5C]">{s.address}</div>}
                  {s.opening_hours && <div className="text-xs text-[#6A6F5C]">{s.opening_hours}</div>}
                </TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{s.extraction_mode || s.type}</Badge></TableCell>
                <TableCell className="max-w-xs truncate">
                  <a href={s.url} target="_blank" rel="noreferrer" className="text-[#E07A5F] hover:underline text-xs">{s.url}</a>
                </TableCell>
                <TableCell>
                  {s.last_run_at ? (
                    <div className="text-xs">
                      <div>{new Date(s.last_run_at).toLocaleString()}</div>
                      <Badge className={s.last_status === "ok" ? "bg-[#81B29A]" : "bg-[#D95D39]"}>
                        {s.last_status}
                      </Badge>
                    </div>
                  ) : <span className="text-xs text-[#9A9E8D]">Never</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => extract(s)} disabled={!!extracting[s.id]} data-testid={`extract-${s.id}`}>
                      {extracting[s.id] ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-${s.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setToDelete(s)} className="text-[#D95D39]" data-testid={`delete-${s.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete source?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{toDelete?.restaurant_name}</strong> and all of its
              extracted lunch specials. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); remove(); }}
              disabled={deleting}
              className="bg-[#D95D39] hover:bg-[#c14e2e] text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Trash2 size={14} className="mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
